const PAGINATION_TYPES = {
  STATIC: 'STATIC',
  LOAD_MORE: 'LOAD_MORE',
}

// Auto-apply "in-stock" availability filter on initial collection load
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-cnstrc-browse]')) return
  const checkbox = document.querySelector(
    'input[name="filter.v.availability"][value="1"]',
  )

  // Exit if checkbox not found (not a collection page / filter not present)
  if (!checkbox) return

  // Skip auto-click if the user is coming from a filter reload
  const url = new URL(window.location.href)
  const hasFilterParam = Array.from(url.searchParams.keys()).some((key) =>
    key.startsWith('filter.'),
  )

  if (hasFilterParam) {
    return
  }

  // Only auto-click if not already checked and not a filter reload
  if (!checkbox.checked) {
    checkbox.click()
  }
})

class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  render (document, event) {
    this.renderProductGrid(document, event)
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderProductGrid (document, event) {
    this.element.throwError(`${this.constructor.name}.render not implemented`)
  }

  syncProductGridAttributes (document) {
    const productGrid = this.element.els['product-grid']?.element
    const nextProductGrid = window.BAO.Utils.getElement(
      this.element.els['product-grid']?.selector,
      { context: document },
    )

    if (!productGrid || !nextProductGrid) return

    Array.from(productGrid.attributes).forEach((attribute) => {
      if (
        attribute.name.startsWith('data-cnstrc-') ||
        attribute.name === 'data-current-page' ||
        attribute.name === 'data-last-page'
      ) {
        productGrid.removeAttribute(attribute.name)
      }
    })

    Array.from(nextProductGrid.attributes).forEach((attribute) => {
      if (
        attribute.name.startsWith('data-cnstrc-') ||
        attribute.name === 'data-current-page' ||
        attribute.name === 'data-last-page'
      ) {
        productGrid.setAttribute(attribute.name, attribute.value)
      }
    })
  }
}

class StaticPaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log(`${this.constructor.name}.renderProductGrid`, {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.Utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML

    this.syncProductGridAttributes(document)
  }
}

class LoadMorePaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (html, e) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    const direction = this.getPaginationDirection(e)
    const newItems = document.createDocumentFragment()
    const newElements = window.BAO.Utils.getElements(
      `[${this.element._elIdentifier}="item"]`,
      {
        context: html,
      },
    )

    newElements.forEach((el) => newItems.appendChild(el))

    if (direction == null) {
      this.element.els['item-container'].element.innerHTML = ''
      this.element.els['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.element.els['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.element.els['item-container'].element.append(newItems)
    }

    this.renderLoadPrevious(html)
    this.renderLoadMore(html)

    document
      .querySelectorAll(`[${this.element._elIdentifier}="item"] img[srcset]`)
      .forEach((image) => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(`[${this.element._elIdentifier}="productpanel"]`)
      .setAttribute('aria-hidden', (newElements.length === 0).toString())
    document
      .querySelector(`[${this.element._elIdentifier}="emptypanel"]`)
      .setAttribute('aria-hidden', (newElements.length > 0).toString())

    this.syncProductGridAttributes(html)

    this.focusNewlyLoadedProductCard(newElements, direction)
  }

  /**
   * Moves keyboard focus to the first newly appended product card
   * after activating the load-more trigger.
   *
   * @param {HTMLElement[]} newElements
   * @param {'NEXT'|'PREVIOUS'|null} direction
   */
  focusNewlyLoadedProductCard (newElements, direction) {
    if (direction !== 'NEXT' || !newElements?.length) {
      return
    }

    const firstNewItem = newElements[0]

    if (!(firstNewItem instanceof HTMLElement)) {
      return
    }

    const focusTarget =
      firstNewItem.querySelector('[data-product-card-el="link"]') ||
      firstNewItem

    if (!(focusTarget instanceof HTMLElement)) {
      return
    }

    if (focusTarget === firstNewItem && !focusTarget.hasAttribute('tabindex')) {
      focusTarget.setAttribute('tabindex', '-1')
    }

    focusTarget.focus()
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.element.els['load-previous'].selector}, ${this.element.els['load-more'].selector}`,
    )

    if (!paginationElement) return null

    return paginationElement === this.element.els['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious && this.element.els['load-previous']) {
      return (this.element.els['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    if (this.element.els['load-previous']) {
      this.element.els['load-previous'].element.innerHTML =
        window.BAO.Utils.getElement(
          this.element.els['load-previous'].selector,
          { context: html },
        ).innerHTML
    }
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    if (this.element.els['load-more']) {
      this.element.els['load-more'].element.innerHTML =
        window.BAO.Utils.getElement(this.element.els['load-more'].selector, {
          context: html,
        }).innerHTML
    }
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!window.BAO.Utils.getElement(
      `${this.element.els.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.element.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.element.els.item?.selector) return 0
    const productGridItems = window.BAO.Utils.getElements(
      `${this.element.els.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = this.getPaginationRenderer()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)

    if (this.element.shouldRenderNativeProductGrid) {
      this.paginationRenderer.render(newHTMLDom, event)
    } else {
      this.paginationRenderer.syncProductGridAttributes(newHTMLDom)
    }

    this.renderProductCount(newHTMLDom)
    this.renderAdditional(newHTMLDom)

    this.element.loading = false
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderFacets (document, event) {
    const getFacetSelector = (facet) => {
      const selectorParts = {
        class: `.${Array.from(facet.classList).join('.')}`,
        additional: `${this.element.els.facet.selector}[data-index="${facet.dataset.index}"]`,
      }

      return Object.values(selectorParts).join('')
    }

    const currentFacetElements = this.element.els.facet.elements
    const facetThatSubmitted = event?.submitter?.closest(
      this.element.els.facet.selector,
    )

    const facetsToRenderSimply = event
      ? currentFacetElements.filter((facet) => facet !== facetThatSubmitted)
      : currentFacetElements

    this.element.log('renderFacets', {
      currentFacetElements,
      facetThatSubmitted,
      facetsToRenderSimply,
    })

    facetsToRenderSimply.forEach((facet) => {
      const selector = getFacetSelector(facet)

      const el = window.BAO.Utils.getElement(selector, { context: document })
      if (!el) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.innerHTML = el.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacets = this.element.els['active-facets'].elements
    if (activeFacets.length <= 0) return

    activeFacets.forEach((activeFacet) => {
      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: `${this.element.els['active-facets'].selector}`,
      }
      if (activeFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.Utils.getElement(selector)
      if (!currentEl) return

      const hasActiveItems = !!window.BAO.Utils.getElement(
        `[${this.element._elIdentifier}="active-facet"]`,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductCount (document) {
    const existingProductCounts = window.BAO.Utils.getElements(
      this.element.els['product-count']?.selector,
    )

    const newProductCount = window.BAO.Utils.getElement(
      this.element.els['product-count']?.selector,
      { context: document },
    )
    if (!newProductCount) return

    existingProductCounts.forEach(
      (element) => (element.innerHTML = newProductCount.innerHTML),
    )
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditional (document) {
    const existingFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
    )
    if (!existingFiltersButtonText) return

    const newFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
      { context: document },
    )
    if (!newFiltersButtonText) return

    existingFiltersButtonText.innerHTML = newFiltersButtonText.innerHTML
  }

  getPaginationRenderer () {
    switch (this.paginationType) {
      case PAGINATION_TYPES.STATIC:
        return new StaticPaginationRenderer(this.element)
      case PAGINATION_TYPES.LOAD_MORE:
        return new LoadMorePaginationRenderer(this.element)
      default:
        return new StaticPaginationRenderer(this.element)
    }
  }

  /**
   * @returns {'STATIC'|'LOAD_MORE'}
   */
  get paginationType () {
    if (!this.element.dataset.paginationType) return PAGINATION_TYPES.STATIC

    return this.element.dataset.paginationType
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get styles () {
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['facets', 'facet', 'facet-summary', 'product-grid']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()

    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  ensureRuntimeState () {
    if (!this.fetcher) {
      this.fetcher = new DataFetcher()
    }

    if (!this.renderer) {
      this.renderer = new Renderer(this)
    }

    if (!this.searchParams) {
      this.searchParams = {
        // eslint-disable-next-line compat/compat
        previous: new URLSearchParams(window.location.search),
        // eslint-disable-next-line compat/compat
        current: new URLSearchParams(window.location.search),
        // eslint-disable-next-line compat/compat
        initial: new URLSearchParams(window.location.search),
      }
    }
  }

  initialize () {
    this.ensureRuntimeState()

    if (this.hasBeenInitialised) {
      return
    }

    this._hasBeenInitialised = true
    this.setupListeners()

    if (this.shadowRoot) {
      this.applyStyles()
    }
  }

  mount () {
    this.initialize()

    this.loading = this.shouldUseConstructorBrowse

    if (this.shouldUseConstructorBrowse) {
      this.setupConstructorBrowse()

      if (
        !this.collectionBrowseStateLayer?.state?.requestInFlight &&
        !this.collectionBrowseStateLayer?.state?.requestMeta
      ) {
        this.collectionBrowseStateLayer?.requestProducts()
      }
    }
  }

  /**
   * Some collection page entry flows can leave this custom element upgraded
   * without its normal instance state and listener setup. Rehydrate the
   * runtime state here so Constructor browse rendering still initializes.
   */
  connectedCallback () {
    this.mount()
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)
    this.onBrowseProductClick = this.onBrowseProductClick.bind(this)

    this.debouncedOnSubmit = window.BAO.Utils.debounce(
      (e) => this.onSubmit(e),
      500,
    )
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(this, 'submit', this.onFormSubmit.bind(this))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(this, 'click', this.onBrowseProductClick)
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const link = event.currentTarget || event.target.closest('a')

    if (!link) {
      return
    }

    const searchParams = new URLSearchParams(link.search)

    this.renderPage({
      browseIntent: 'replace',
      searchParams: searchParams.toString(),
    })
  }

  onSubmit (event) {
    event.preventDefault()

    this.lastSelectedConstructorFacet =
      event.target.closest('.clc-ProductGridFacets_Item[data-cnstrc-field]')
        ?.dataset.cnstrcField || null

    this.capturePendingConstructorFocus(event)

    const parentForm = event.target.closest('form')

    parentForm.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    )
  }

  onFormSubmit (event) {
    event.preventDefault()

    const formData = new FormData(event.target)
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({
      browseIntent: 'replace',
      event,
      resetBrowsePage: this.shouldUseConstructorBrowse,
      searchParams,
    })

    if (window.BAO.MediaBreakpoints.isSm) return

    if (!this.shouldUseConstructorBrowse) {
      this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams =
      event.state?.searchParams ?? window.location.search.slice(1)
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({
      browseIntent: 'replace',
      searchParams,
      updateURLHash: false,
    })
  }

  async onPaginationClick (event) {
    const pagination = event.target.closest(this.els?.pagination?.selector)
    if (!pagination) return

    const anchor = event.target.closest(
      'a[data-collection-filters-el="pagination-trigger"]',
    )

    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) {
      return
    }

    event.preventDefault()

    const browseIntent = this.getBrowsePaginationIntent(anchor)

    if (!browseIntent) {
      this.scrollProductGridToTop()
    }

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({
      browseIntent: browseIntent || 'replace',
      searchParams: currentSearchParams.toString(),
      event,
    })
  }

  onBrowseProductClick (event) {
    if (!this.shouldUseConstructorBrowse) {
      return
    }

    const link = event.target.closest('[data-product-card-el="link"]')
    const productCard = event.target.closest('product-card')

    if (!link || !productCard?.dataset.propData) {
      return
    }

    const product = JSON.parse(productCard.dataset.propData)
    const listItem = link.closest('[data-collection-filters-el="item"]')

    this.collectionBrowseEventBus?.publish(
      'collectionBrowse:tracking:product:clicked',
      {
        itemName: product.itemName,
        productId: product.productId,
        resultPositionOnPage:
          Number(listItem?.dataset.resultPositionOnPage) || 0,
        sourcePage: Number(listItem?.dataset.sourcePage) || this.currentPage,
        variationId: product.variationId,
      },
    )
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({
    searchParams,
    event,
    updateURLHash = true,
    browseIntent = 'replace',
    resetBrowsePage = false,
  } = {}) {
    this.log('renderPage', { searchParams, event, updateURLHash })

    const nextSearchParams = new URLSearchParams(searchParams)

    if (resetBrowsePage) {
      nextSearchParams.delete('page')
    }

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = nextSearchParams
    this.syncConstructorFacetInputsFromSearchParams()

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    if (this.shouldUseConstructorBrowse) {
      this.renderConstructorActiveFacetsFromInputs()
      this.collectionBrowseEventBus?.publish(
        'collectionBrowse:ui:params:changed',
        {
          intent: browseIntent,
          searchParams: this.searchParams.current,
        },
      )
      return
    }

    for (const section of this.sections) {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      await this.renderer.render(html, event)
    }
  }

  syncConstructorFacetInputsFromSearchParams () {
    if (!this.shouldUseConstructorBrowse) {
      return
    }

    const forms = this.querySelectorAll(
      '#CollectionFiltersForm, #CollectionFiltersMobileForm',
    )

    forms.forEach((form) => {
      form.querySelectorAll('input[name^="filter."]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {
          return
        }

        if (input.type === 'checkbox' || input.type === 'radio') {
          const activeValues = this.searchParams.current.getAll(input.name)
          input.checked = activeValues.includes(input.value)
          return
        }

        input.value = this.searchParams.current.get(input.name) || ''
      })
    })
  }

  setupConstructorBrowse () {
    this.ensureRuntimeState()

    if (this.constructorBrowseSetup) {
      return
    }

    const eventBus = this.collectionBrowseEventBus

    if (!eventBus) {
      return
    }

    this.onConstructorProductsUpdated = async ({
      pagination,
      products,
      request,
      selectedFilters,
      sortBy,
      sortOrder,
    }) => {
      const intent = pagination.lastIntent || 'replace'
      const { productCards, promotionItems, renderId } =
        this.renderBrowseProducts(products, { intent })
      this.renderConstructorPagination({
        ...pagination,
        total: 0,
        totalPages: 0,
      })
      this.renderConstructorProductCount({
        ...pagination,
        total: 0,
      })
      this.toggleBrowseFilterVisibility(false)
      this.updateBrowseTrackingAttributes({
        products: {
          resultId: pagination.resultId,
          total: pagination.total,
        },
      })
      this.setCnstrcData({
        request,
        resultId: pagination.resultId,
      })
      this.trackBrowseResultsLoaded({
        products: {
          hits: products,
          resultId: pagination.resultId,
          total: pagination.total,
        },
        request,
        selectedFilters,
        sortBy,
        sortOrder,
      })

      await this.revealBrowsePromotionItemsWhenReady({
        productCards,
        promotionItems,
        renderId,
      })

      await this.constructorApiHelpers.waitForProductCards(productCards)

      this.syncBrowseRenderedState(pagination)
      this.updateCollectionItemListSchema(products, intent)

      this.loading = false
    }

    this.onConstructorFacetsUpdated = async ({ facets }) => {
      this.renderConstructorFacets(facets)
      this.renderConstructorActiveFacetsFromInputs()
      this.restorePendingConstructorFocus()
      this.latestConstructorFacets = facets
    }

    eventBus.subscribe(
      'collectionBrowse:state:products:updated',
      this.onConstructorProductsUpdated,
    )
    eventBus.subscribe(
      'collectionBrowse:state:facets:updated',
      this.onConstructorFacetsUpdated,
    )

    this.renderConstructorFacets([])
    this.renderConstructorActiveFacetsFromInputs()

    this.constructorBrowseSetup = true
  }

  updateCollectionItemListSchema (products = [], intent = 'replace') {
    const schemaScript = document.getElementById(
      'seo-schema-collection-page-group',
    )

    if (!schemaScript) return

    let schema

    try {
      schema = JSON.parse(schemaScript.textContent)
    } catch (e) {
      console.error('Unable to parse schema', e)
      return
    }

    if (!Array.isArray(schema['@graph'])) return

    const canonical =
      document.querySelector('link[rel="canonical"]')?.href ||
      window.location.href

    const nonPromotionProducts = products.filter(
      (product) => product.itemType !== 'promotion',
    )

    const newItems = nonPromotionProducts.map((product) => ({
      '@type': 'ListItem',
      name: product.itemName,
      url: product.url,
    }))

    let itemList = schema['@graph'].find(
      (item) => item['@type'] === 'ItemList',
    )

    if (!itemList || intent === 'replace') {
      itemList = {
        '@type': 'ItemList',
        '@id': `${canonical}#itemlist`,
        itemListElement: newItems,
      }

      schema['@graph'] = schema['@graph'].filter(
        (item) => item['@type'] !== 'ItemList',
      )

      schema['@graph'].push(itemList)
    } else if (intent === 'append-next') {
      itemList.itemListElement = [
        ...(itemList.itemListElement || []),
        ...newItems,
      ]
    } else if (intent === 'prepend-previous') {
      itemList.itemListElement = [
        ...newItems,
        ...(itemList.itemListElement || []),
      ]
    }

    itemList.itemListElement = itemList.itemListElement.map((item, index) => ({
      ...item,
      position: index + 1,
    }))

    itemList.numberOfItems = itemList.itemListElement.length

    schemaScript.textContent = JSON.stringify(schema)
  }

  setLastSelectedConstructorFacetFromEvent (event) {
    if (!this.shouldUseConstructorBrowse) {
      return
    }

    const input = event.target

    if (!(input instanceof HTMLInputElement)) {
      return
    }

    const field = this.getConstructorFacetFieldFromInput(input)

    if (!field) {
      return
    }

    this.lastSelectedConstructorFacet = field
  }

  getConstructorFacetFieldFromInput (input) {
    if (input.dataset.cnstrcField) {
      return input.dataset.cnstrcField
    }

    if (
      input.name === 'filter.v.price.gte' ||
      input.name === 'filter.v.price.lte'
    ) {
      return 'price'
    }

    return ''
  }

  renderBrowseProducts (items, { intent = 'replace' } = {}) {
    const itemContainer = this.els['item-container']?.element
    const productCards = []
    const promotionItems = []
    const productTemplate = this.templateProductCard
    const renderId = (this.browseRenderId || 0) + 1
    const hasPromotionItems = items.some((item) => {
      return item?.itemType === 'promotion'
    })

    this.browseRenderId = renderId

    if (!itemContainer) {
      return {
        productCards,
        promotionItems,
        renderId,
      }
    }

    if (intent === 'replace') {
      itemContainer.innerHTML = ''
    }

    if (items.length === 0) {
      if (intent === 'replace') {
        this.setProductPanelState({ hasProducts: false })
      }

      return {
        productCards,
        promotionItems,
        renderId,
      }
    }

    if (
      !productTemplate ||
      (hasPromotionItems && !this.templatePromotionCard)
    ) {
      this.deferBrowseProductRender(items)
      return {
        productCards,
        promotionItems,
        renderId,
      }
    }

    const renderedItems = []

    items.forEach((item) => {
      if (item?.itemType === 'promotion') {
        const renderedPromotion =
          this.constructorApiHelpers.createPromotionCardItem({
            itemDataset: {
              collectionFiltersEl: 'promotion',
              page: item.sourcePage || this.currentPage,
              sourcePage: item.sourcePage || this.currentPage,
            },
            promotion: item,
            promotionTemplate: this.templatePromotionCard,
          })

        if (renderedPromotion?.cardItem) {
          this.constructorApiHelpers.setPromotionCardVisibility(
            renderedPromotion.cardItem,
            false,
          )
          renderedItems.push(renderedPromotion.cardItem)
          promotionItems.push(renderedPromotion.cardItem)
        }

        return
      }

      const renderedProduct = this.constructorApiHelpers.createProductCardItem({
        itemDataset: {
          collectionFiltersEl: 'item',
          page: item.sourcePage || this.currentPage,
          resultPositionOnPage: item.resultPositionOnPage || 0,
          sourcePage: item.sourcePage || this.currentPage,
        },
        product: { ...item, collectionHandle: this.browseFilterValue },
        productTemplate: this.templateProductCard,
      })

      if (!renderedProduct) {
        return
      }

      renderedItems.push(renderedProduct.cardItem)
      productCards.push(renderedProduct.productCard)
    })

    if (intent === 'prepend-previous') {
      const fragment = document.createDocumentFragment()
      renderedItems.forEach((item) => fragment.appendChild(item))
      itemContainer.prepend(fragment)
    } else {
      renderedItems.forEach((item) => itemContainer.appendChild(item))
    }

    this.setProductPanelState({ hasProducts: true })

    return {
      productCards,
      promotionItems,
      renderId,
    }
  }

  async revealBrowsePromotionItemsWhenReady ({
    productCards = [],
    promotionItems = [],
    renderId,
  } = {}) {
    const firstRowProductCards = productCards.slice(
      0,
      this.getBrowseFirstRowCount(productCards.length),
    )

    await this.constructorApiHelpers.waitForProductCards(firstRowProductCards)

    if (renderId !== this.browseRenderId) {
      return
    }

    promotionItems.forEach((promotionItem) => {
      if (!promotionItem?.isConnected) {
        return
      }

      this.constructorApiHelpers.setPromotionCardVisibility(
        promotionItem,
        true,
      )
    })
  }

  syncBrowseRenderedState (pagination = {}) {
    const visibleProductCount = this.getVisibleBrowseProductCount()

    if (visibleProductCount > 0) {
      this.setProductPanelState({ hasProducts: true })
      this.renderConstructorPagination(pagination)
      this.renderConstructorProductCount(pagination)
      this.toggleBrowseFilterVisibility(true)
      return
    }

    const resolvedPagination = {
      ...pagination,
      total: 0,
      totalPages: 0,
    }

    this.setProductPanelState({ hasProducts: false })
    this.renderConstructorPagination(resolvedPagination)
    this.renderConstructorProductCount(resolvedPagination)
    this.renderConstructorFacets([])
    this.toggleBrowseFilterVisibility(false)
  }

  getVisibleBrowseProductCount () {
    const itemContainer = this.els['item-container']?.element

    if (!itemContainer) {
      return 0
    }

    return itemContainer.querySelectorAll(
      '[data-collection-filters-el="item"] product-card',
    ).length
  }

  toggleBrowseFilterVisibility (isVisible = true) {
    const desktopFacets = this.querySelector(
      '#CollectionFiltersForm .clc-ProductGridFacets_Facets',
    )
    const fixedFilters = this.querySelector('.clc-ProductGrid_FixedFilters')
    const drawer = document.querySelector(
      'dialog[is="site-drawer"][key="collection-filters"]',
    );

    [desktopFacets, fixedFilters, drawer].forEach((element) => {
      if (!element) {
        return
      }

      element.hidden = !isVisible
    })
  }

  getBrowseFirstRowCount (productCount = 0) {
    if (productCount <= 0) {
      return 0
    }

    const itemContainer = this.els['item-container']?.element

    if (!itemContainer) {
      return productCount
    }

    const gridTemplateColumns = window
      .getComputedStyle(itemContainer)
      .gridTemplateColumns.trim()

    if (!gridTemplateColumns) {
      return productCount
    }

    const columnCount = gridTemplateColumns.split(' ').filter(Boolean).length

    return Math.min(productCount, columnCount || productCount)
  }

  setProductPanelState ({ hasProducts }) {
    const productPanel = this.els.productpanel?.element
    const emptyPanel = this.els.emptypanel?.element

    if (productPanel) {
      productPanel.setAttribute('aria-hidden', String(!hasProducts))
    }

    if (emptyPanel) {
      emptyPanel.setAttribute('aria-hidden', String(hasProducts))
    }
  }

  getActiveBrowseFilters () {
    const activeFilters = []
    const seenFilters = new Set()
    const priceRange = {
      min: null,
      max: null,
    }
    const appendFilter = (filter) => {
      const filterKey = `${filter.field}::${filter.value}`

      if (!filter.field || !filter.value || seenFilters.has(filterKey)) {
        return
      }

      seenFilters.add(filterKey)
      activeFilters.push(filter)
    }
    const forms = document.querySelectorAll(
      '#CollectionFiltersForm, #CollectionFiltersMobileForm',
    )

    forms.forEach((form) => {
      form
        .querySelectorAll('input[name^="filter."], input[data-cnstrc-field]')
        .forEach((input) => {
          if (!(input instanceof HTMLInputElement)) {
            return
          }

          if (input.name === 'filter.v.price.gte') {
            if (input.value !== '' && priceRange.min == null) {
              priceRange.min = input.value
            }

            return
          }

          if (input.name === 'filter.v.price.lte') {
            if (input.value !== '' && priceRange.max == null) {
              priceRange.max = input.value
            }

            return
          }

          if (input.name === 'filter.v.availability') {
            return
          }

          if (!input.checked) {
            return
          }

          appendFilter({
            type: 'list',
            field: this.collectionBrowseApiLayer.getBrowseFieldLabel(input),
            inputName: input.name,
            inputValue: input.value,
            value: this.collectionBrowseApiLayer.getBrowseValueLabel(input),
          })
        })
    })

    this.searchParams.current.forEach((value, key) => {
      if (!key.startsWith('filter.')) {
        return
      }

      if (key === 'filter.v.price.gte') {
        if (priceRange.min == null && value !== '') {
          priceRange.min = value
        }

        return
      }

      if (key === 'filter.v.price.lte') {
        if (priceRange.max == null && value !== '') {
          priceRange.max = value
        }

        return
      }

      if (key === 'filter.v.availability') {
        return
      }

      appendFilter({
        type: 'list',
        field: this.collectionBrowseApiLayer.getFilterFieldFromParamName(key),
        inputName: key,
        inputValue: value,
        value,
      })
    })

    if (priceRange.min != null || priceRange.max != null) {
      activeFilters.push({
        type: 'price_range',
        max: priceRange.max,
        min: priceRange.min,
      })
    }

    return activeFilters
  }

  renderConstructorFacets (facets = []) {
    this.renderConstructorDesktopFacets(facets)
    this.renderConstructorDrawerFacets(facets)
  }

  renderConstructorDesktopFacets (facets = []) {
    const container = this.querySelector(
      '#CollectionFiltersForm .clc-ProductGridFacets_Items',
    )

    if (!container) {
      return
    }

    const preservedInputs = this.getPreservedFacetInputsMarkup(container)
    const desktopFacets = facets.filter((facet) => {
      const normalizedField = String(facet?.field || '')
        .trim()
        .toLowerCase()

      return ['category', 'gender', 'size', 'price'].includes(normalizedField)
    })
    const facetMarkup = desktopFacets
      .map((facet) => {
        const label = this.escapeHTML(facet.name || facet.field || '')

        if (!label) {
          return ''
        }

        return `
          <div
            class="clc-ProductGridFacets_Item"
            aria-expanded="false"
          >
            <div class="clc-ProductGridFacet">
              <button
                type="button"
                class="clc-ProductGridFacet_Name"
                data-drawers-trigger="collection-filters"
                data-facet="${label}"
              >
                <span class="clc-ProductGridFacet_FilterLabel fz-13_140">
                  ${label}
                </span>

                ${this.getFacetChevronMarkup()}
              </button>
            </div>
          </div>
        `
      })
      .filter(Boolean)
      .join('')

    container.innerHTML = `${facetMarkup}${preservedInputs}`
  }

  renderConstructorDrawerFacets (facets = []) {
    const container = this.querySelector(
      '#CollectionFiltersMobileForm .clc-ProductGridFacets_Items',
    )

    if (!container) {
      return
    }

    this.constructorDrawerExpandedFields =
      this.getConstructorDrawerExpandedFields(container)

    const preservedInputs = this.getPreservedFacetInputsMarkup(container)
    const facetMarkup = facets
      .map((facet, index) => this.getConstructorDrawerFacetMarkup(facet, index))
      .filter(Boolean)
      .join('')

    container.innerHTML = `${facetMarkup}${preservedInputs}`
  }

  getConstructorDrawerFacetMarkup (facet = {}, index = 0) {
    if (facet.type === 'range') {
      return this.getConstructorDrawerRangeFacetMarkup(facet, index)
    }

    const label = this.escapeHTML(facet.name || facet.field || '')
    const field = this.escapeHTML(facet.field || '')
    const isExpanded = this.isConstructorDrawerFacetExpanded(facet)
    const optionMarkup = Array.isArray(facet.data)
      ? facet.data
        .map((value, optionIndex) => {
          return this.getConstructorDrawerFacetOptionMarkup({
            facet,
            field,
            label,
            optionIndex,
            value,
          })
        })
        .filter(Boolean)
        .join('')
      : ''

    if (!label || !field || !optionMarkup) {
      return ''
    }

    return `
      <div
        class="clc-ProductGridFacets_Item"
        data-cnstrc-field="${field}"
        data-filter-accordions-el="item"
        data-drawers-accordion="${label}"
        role="treeitem"
      >
        <div class="clc-ProductGridFacet" data-expanded="${isExpanded}">
          <h3 class="clc-ProductGridFacet_Heading">
            <button
              type="button"
              class="clc-ProductGridFacet_Name"
              data-collection-filters-el="facet-summary"
              data-filter-accordions-el="trigger"
              aria-expanded="${isExpanded}"
              aria-label="Filter options: ${label}"
            >
              <span class="clc-ProductGridFacet_FilterLabel fz-16_130">
                ${label}
              </span>

            <span class="clc-ProductGridFacet_LabelIcon">
              ${this.getFacetChevronMarkup()}
            </span>
          </button>

          <div
            class="clc-ProductGridFacet_Options"
            data-collection-filters-el="facet"
            data-index="constructor-${index + 1}"
            data-filter-accordions-el="item"
            ${isExpanded ? '' : 'inert'}
          >
            <ul class="clc-ProductGridFacet_List" role="group" aria-label="Filter by ${label}">
              ${optionMarkup}
            </ul>
          </div>
        </div>
      </div>
    `
  }

  getConstructorDrawerRangeFacetMarkup (facet = {}, index = 0) {
    if (facet.field !== 'price') {
      return ''
    }

    const label = this.escapeHTML(facet.name || facet.field || '')
    const range = this.getConstructorRangeValues(facet)

    if (!label || !range) {
      return ''
    }

    const isExpanded = this.isConstructorDrawerFacetExpanded(facet)
    const inputId = this.escapeHTML(
      `Constructor-${facet.field || 'price'}-${index + 1}`,
    )
    const minValueAttribute =
      range.selectedMin == null
        ? ''
        : `min-value="${this.escapeHTML(range.selectedMin)}"`
    const maxValueAttribute =
      range.selectedMax == null
        ? ''
        : `max-value="${this.escapeHTML(range.selectedMax)}"`
    const minInputValue =
      range.selectedMin == null ? '' : this.escapeHTML(range.selectedMin)
    const maxInputValue =
      range.selectedMax == null ? '' : this.escapeHTML(range.selectedMax)
    const minLabel = this.formatFacetMoney(range.selectedMin ?? range.min)
    const maxLabel = this.formatFacetMoney(range.selectedMax ?? range.max)
    const rangeMin = this.escapeHTML(range.min)
    const rangeMax = this.escapeHTML(range.max)
    const fromLabel = this.escapeHTML(
      window.theme?.strings?.priceRangeFrom || '',
    )
    const toLabel = this.escapeHTML(window.theme?.strings?.priceRangeTo || '')
    const facetHeadingId = this.escapeHTML(`${inputId}-Label`)

    return `
      <div
        class="clc-ProductGridFacets_Item"
        data-cnstrc-field="${this.escapeHTML(facet.field || '')}"
        data-filter-accordions-el="item"
        data-drawers-accordion="${label}"
      >
        <div class="clc-ProductGridFacet" data-expanded="${isExpanded}">
          <button
            type="button"
            class="clc-ProductGridFacet_Name"
            data-collection-filters-el="facet-summary"
            data-filter-accordions-el="trigger"
            aria-expanded="${isExpanded}"
          >
            <span id="${facetHeadingId}" class="clc-ProductGridFacet_FilterLabel fz-16_130">
              ${label}
            </span>

            <span class="clc-ProductGridFacet_LabelIcon">
              ${this.getFacetChevronMarkup()}
            </span>
          </button>

          <div
            class="clc-ProductGridFacet_Options"
            data-collection-filters-el="facet"
            data-index="constructor-${index + 1}"
            data-filter-accordions-el="item"
            ${isExpanded ? '' : 'inert'}
          >
            <div class="clc-ProductGridFacet_Option">
              <div class="clc-ProductGridFacet_PriceRange">
                <range-slider
                  class="clc-PriceRange"
                  min="${rangeMin}"
                  max="${rangeMax}"
                  ${minValueAttribute}
                  ${maxValueAttribute}
                >
                  <button
                    class="clc-PriceRange_Dot clc-PriceRange_Dot-left"
                    type="button"
                    role="slider"
                    tabindex="0"
                    aria-label="${label} ${fromLabel}"
                    aria-valuemin="${rangeMin}"
                    aria-valuemax="${this.escapeHTML(
                      range.selectedMax ?? range.max,
                    )}"
                    aria-valuenow="${this.escapeHTML(
                      range.selectedMin ?? range.min,
                    )}"
                    data-value-label="${minLabel}"
                    data-range-slider-el="touchLeft"
                  >
                    <span class="clc-PriceRange_DotInner"></span>
                  </button>

                  <button
                    class="clc-PriceRange_Dot clc-PriceRange_Dot-right"
                    type="button"
                    role="slider"
                    tabindex="0"
                    aria-label="${label} ${toLabel}"
                    aria-valuemin="${this.escapeHTML(
                      range.selectedMin ?? range.min,
                    )}"
                    aria-valuemax="${rangeMax}"
                    aria-valuenow="${this.escapeHTML(
                      range.selectedMax ?? range.max,
                    )}"
                    data-value-label="${maxLabel}"
                    data-range-slider-el="touchRight"
                  >
                    <span class="clc-PriceRange_DotInner"></span>
                  </button>

                  <div class="clc-PriceRange_Line" data-range-slider-el="line">
                    <span class="clc-PriceRange_LineInner"></span>
                    <span class="clc-PriceRange_LineHighlighter" data-range-slider-el="highlighter"></span>
                  </div>

                  <input
                    class="clc-PriceRange_Input"
                    name="filter.v.price.gte"
                    id="${inputId}-GTE"
                    value="${minInputValue}"
                    type="hidden"
                    placeholder="${rangeMin}"
                    min="${rangeMin}"
                    max="${rangeMax}"
                    data-range-slider-el="min"
                  >

                  <input
                    class="clc-PriceRange_Input"
                    name="filter.v.price.lte"
                    id="${inputId}-LTE"
                    value="${maxInputValue}"
                    type="hidden"
                    min="${rangeMin}"
                    placeholder="${rangeMax}"
                    max="${rangeMax}"
                    data-range-slider-el="max"
                  >
                </range-slider>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  capturePendingConstructorFocus (event) {
    if (!this.shouldUseConstructorBrowse) {
      this.pendingConstructorFocus = null
      return
    }

    const target = event?.target

    if (!(target instanceof HTMLElement)) {
      this.pendingConstructorFocus = null
      return
    }

    const facetItem = target.closest(
      '.clc-ProductGridFacets_Item[data-cnstrc-field]',
    )

    if (!facetItem) {
      this.pendingConstructorFocus = null
      return
    }

    const field = facetItem.dataset.cnstrcField

    if (!field) {
      this.pendingConstructorFocus = null
      return
    }

    const activeElement = document.activeElement
    const thumbSelector =
      '[data-range-slider-el="touchLeft"], [data-range-slider-el="touchRight"]'
    const activeThumb =
      activeElement instanceof HTMLElement &&
      activeElement.matches(thumbSelector)
        ? activeElement
        : null

    this.pendingConstructorFocus = {
      field,
      thumb: activeThumb?.getAttribute('data-range-slider-el') || null,
    }
  }

  restorePendingConstructorFocus () {
    if (!this.shouldUseConstructorBrowse || !this.pendingConstructorFocus) {
      return
    }

    const { field, thumb } = this.pendingConstructorFocus
    const facetItem = this.querySelector(
      `.clc-ProductGridFacets_Item[data-cnstrc-field="${field}"]`,
    )

    if (!facetItem) {
      this.pendingConstructorFocus = null
      return
    }

    let focusTarget = null

    if (thumb) {
      focusTarget = facetItem.querySelector(
        `[data-range-slider-el="${thumb}"]`,
      )
    }

    if (!focusTarget) {
      focusTarget = facetItem.querySelector(
        '[data-filter-accordions-el="trigger"]',
      )
    }

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true })
    }

    this.pendingConstructorFocus = null
  }

  getConstructorDrawerExpandedFields (container) {
    const expandedFacetElements = container.querySelectorAll(
      '.clc-ProductGridFacets_Item[data-cnstrc-field] .clc-ProductGridFacet[data-expanded="true"]',
    )

    return new Set(
      Array.from(expandedFacetElements)
        .map((facetEl) => {
          return facetEl.closest('.clc-ProductGridFacets_Item')?.dataset
            .cnstrcField
        })
        .filter(Boolean),
    )
  }

  isConstructorDrawerFacetExpanded (facet = {}) {
    if (!facet.field) {
      return false
    }

    return (
      this.constructorDrawerExpandedFields?.has(facet.field) ||
      this.lastSelectedConstructorFacet === facet.field
    )
  }

  getConstructorRangeValues (facet = {}) {
    const min = Number.parseFloat(facet.min)
    const max = Number.parseFloat(facet.max)

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null
    }

    const selectedMin = Number.parseFloat(
      this.searchParams.current.get('filter.v.price.gte'),
    )
    const selectedMax = Number.parseFloat(
      this.searchParams.current.get('filter.v.price.lte'),
    )

    const rangeMin = this.constructorApiHelpers.priceRangeMin
    const rangeMax = this.constructorApiHelpers.normalizeRangeMaxValue(
      Math.max(min, max),
      rangeMin,
    )

    return {
      max: rangeMax,
      min: rangeMin,
      selectedMax: this.constructorApiHelpers.normalizeRangeMaxValue(
        selectedMax,
        null,
      ),
      selectedMin: this.constructorApiHelpers.normalizeRangeMinValue(
        selectedMin,
        null,
      ),
    }
  }

  getConstructorDrawerFacetOptionMarkup ({
    facet = {},
    field = '',
    label = '',
    optionIndex = 0,
    value = {},
  } = {}) {
    const optionLabel = this.escapeHTML(value.value || '')
    const optionValue = this.escapeHTML(value.value || '')
    const inputId = this.escapeHTML(
      `Constructor-${facet.field || 'facet'}-${optionIndex + 1}`,
    )
    const optionCount = Number(value.count) || 0
    const inputName = this.escapeHTML(`filter.cnstrc.${facet.field || field}`)

    if (!optionLabel || !optionValue || !inputName || !label) {
      return ''
    }

    return `
      <li class="clc-ProductGridFacet_Item">
        <label
          class="clc-ProductGridFacet_Label"
          for="${inputId}"
          aria-disabled="${optionCount === 0 && !value.selected}"
        >
          <input
            class="clc-ProductGridFacet_Checkbox"
            type="checkbox"
            name="${inputName}"
            value="${optionValue}"
            id="${inputId}"
            data-cnstrc-field="${field}"
            data-cnstrc-value="${optionValue}"
            ${value.selected ? 'checked' : ''}
            ${optionCount === 0 && !value.selected ? 'disabled' : ''}
          >
          <span class="clc-ProductGridFacet_CheckboxIcon">
            ${this.getFacetTickMarkup()}
          </span>
          ${optionLabel} (${optionCount})
        </label>
      </li>
    `
  }

  getPreservedFacetInputsMarkup (container) {
    return Array.from(container.querySelectorAll('input[type="hidden"]'))
      .map((input) => input.outerHTML)
      .join('')
  }

  getFacetChevronMarkup () {
    return `
      <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M1 1.25L5.5 5.75L10 1.25" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
  }

  getFacetTickMarkup () {
    return `
      <svg width="10" height="8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M8.6 1 2.932 6.7 1 4.757" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
  }

  escapeHTML (value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }

  get collectionBrowseApiLayer () {
    return window.theme.collectionBrowse.apiLayer
  }

  get constructorApiHelpers () {
    return window.theme.constructorApiHelpers
  }

  updateBrowseTrackingAttributes (browseResponse) {
    const productGrid = this.els['product-grid']?.element

    if (!productGrid) {
      return
    }

    productGrid.setAttribute(
      'data-cnstrc-num-results',
      String(browseResponse.products.total || 0),
    )
    productGrid.setAttribute(
      'data-cnstrc-result-page',
      String(this.currentPage),
    )

    if (browseResponse.products.resultId) {
      productGrid.setAttribute(
        'data-cnstrc-result-id',
        browseResponse.products.resultId,
      )
    } else {
      productGrid.removeAttribute('data-cnstrc-result-id')
    }

    if (browseResponse.products.total === 0) {
      productGrid.setAttribute('data-cnstrc-zero-result', '')
    } else {
      productGrid.removeAttribute('data-cnstrc-zero-result')
    }
  }

  setCnstrcData ({ request, resultId } = {}) {
    if (!request || !resultId) {
      this.cnstrcDataScript?.remove()
      return
    }

    const script = this.cnstrcDataScript || this.createCnstrcDataScript()

    script.textContent = JSON.stringify({
      request,
      result_id: resultId,
    })
  }

  trackBrowseResultsLoaded (browseResponse) {
    const trackedProducts = browseResponse.products.hits.filter((item) => {
      return item?.itemType !== 'promotion'
    })

    if (!this.tracker || trackedProducts.length === 0) {
      return
    }

    this.tracker.trackBrowseResultsLoaded({
      filterName: this.browseFilterName,
      filterValue: this.browseFilterValue,
      items: trackedProducts.map((product) => ({
        itemId: product.productId,
      })),
      resultCount: browseResponse.products.total,
      resultId: browseResponse.products.resultId,
      resultPage: this.currentPage,
      selectedFilters: browseResponse.selectedFilters,
      sortBy: browseResponse.sortBy || this.currentSortBy,
      sortOrder: browseResponse.sortOrder,
      url: window.location.href,
    })
  }

  renderConstructorPagination (pagination = {}) {
    const loadPreviousElement = this.els['load-previous']?.element
    const loadMoreElement = this.els['load-more']?.element
    const productGridPanelId = this.els['product-grid']?.element?.id
    const controlsAttribute = productGridPanelId
      ? `aria-controls="${this.escapeHTML(productGridPanelId)}"`
      : ''
    const lowestLoadedPage =
      pagination.lowestLoadedPage || pagination.currentPage || 1
    const highestLoadedPage =
      pagination.highestLoadedPage || pagination.currentPage || 1
    const pageSize = pagination.size || this.resultsPerPage
    const totalCount = pagination.total || 0
    const shouldShowLoadPrevious = lowestLoadedPage > 1
    const shouldShowLoadMore = highestLoadedPage < (pagination.totalPages || 1)
    const showingFrom =
      totalCount > 0 ? (lowestLoadedPage - 1) * pageSize + 1 : 0
    const showingCount = Math.min(highestLoadedPage * pageSize, totalCount)

    if (loadPreviousElement) {
      loadPreviousElement.innerHTML = shouldShowLoadPrevious
        ? `
            <div class="pgn-LoadMore pgn-LoadMore-previous">
              <a
                class="pgn-LoadMore_Link btn-Button btn-Button-primary"
                href="${this.getPaginationHref(lowestLoadedPage - 1)}"
                data-collection-filters-el="pagination-trigger"
                ${controlsAttribute}
              >
                ${this.loadMoreLabel}
              </a>
            </div>
          `
        : ''
    }

    if (loadMoreElement) {
      if (totalCount === 0) {
        loadMoreElement.innerHTML = ''
        return
      }

      loadMoreElement.innerHTML = `
        <div class="pgn-LoadMore">
          <p class="pgn-LoadMore_Text fz-16_140">
            ${this.showingCountLabel
              .replace('__FROM__', showingFrom)
              .replace('__COUNT__', showingCount)
              .replace('__TOTAL__', totalCount)}
          </p>

          ${
            shouldShowLoadMore
              ? `
              <a
                class="pgn-LoadMore_Link btn-Button btn-Button-primary"
                href="${this.getPaginationHref(highestLoadedPage + 1)}"
                data-collection-filters-el="pagination-trigger"
                ${controlsAttribute}
              >
                ${this.loadMoreLabel}
              </a>
            `
              : ''
          }

          <a href="#MainContent" class="pgn-LoadMore_BackTop fz-16_140">
            ${this.backTopLabel}
          </a>
        </div>
      `
    }
  }

  renderConstructorProductCount (pagination = {}) {
    const existingProductCounts = window.BAO.Utils.getElements(
      this.els['product-count']?.selector,
    )

    if (!existingProductCounts.length) {
      return
    }

    existingProductCounts.forEach((element) => {
      element.textContent = `${pagination.total || 0}`
    })
  }

  renderConstructorActiveFacetsFromInputs () {
    const activeFacetContainers = this.els['active-facets']?.elements || []

    if (activeFacetContainers.length === 0) {
      return
    }

    const activeFilters = this.getActiveBrowseFilters().filter((filter) => {
      if (filter.type === 'list') {
        return (
          filter.field && filter.value && filter.inputName && filter.inputValue
        )
      }

      if (filter.type === 'price_range') {
        return filter.min != null || filter.max != null
      }

      return false
    })

    activeFacetContainers.forEach((container, containerIndex) => {
      container.innerHTML = ''
      container.dataset.empty = (activeFilters.length === 0).toString()

      const removeLabel = container.dataset.removeLabel || 'Remove'

      activeFilters.forEach((filter, filterIndex) => {
        const label =
          filter.type === 'price_range'
            ? this.getActivePriceRangeLabel(filter)
            : filter.value
        const href =
          filter.type === 'price_range'
            ? this.getPriceRangeRemovalHref(filter)
            : this.getActiveFacetRemovalHref(filter)
        const iconId = `ActiveFacetRemoveIcon-${containerIndex}-${filterIndex}`

        container.insertAdjacentHTML(
          'beforeend',
          `
            <facet-remove
              class="clc-ProductGridFacets_ActiveFacet"
              data-collection-filters-el="active-facet"
            >
              <a
                class="clc-ProductGridFacets_ActiveLink"
                href="${href}"
                data-facet-remove-el="link"
              >
                ${label}

                <svg
                  role="img"
                  aria-labelledby="${iconId}"
                  width="12"
                  height="13"
                  viewBox="0 0 12 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title id="${iconId}">${removeLabel}</title>
                  <path
                    d="M8.48627 9.32917L2.82849 3.67098"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                  <path
                    d="M2.88539 9.38504L8.42932 3.61524"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </a>
            </facet-remove>
          `,
        )
      })

      if (activeFilters.length > 0) {
        container.insertAdjacentHTML(
          'beforeend',
          `
            <facet-remove class="clc-ProductGridFacets_ActiveFacet">
              <a
                href="${this.getClearAllHref()}"
                data-facet-remove-el="link"
                class="clc-ProductGridFacets_ActiveLink clc-ProductGridFacets_ActiveLink-plain btn-Button btn-Button-underlined"
              >
                ${this.getClearAllLabel()}
              </a>
            </facet-remove>
          `,
        )
      }
    })
  }

  getActiveFacetRemovalHref (filter = {}) {
    const params = new URLSearchParams(this.searchParams.current.toString())
    const nextParams = new URLSearchParams()

    params.forEach((value, key) => {
      if (key === filter.inputName && value === filter.inputValue) {
        return
      }

      if (key === 'page') {
        return
      }

      nextParams.append(key, value)
    })

    const nextQuery = nextParams.toString()

    return nextQuery
      ? `${window.location.pathname}?${nextQuery}`
      : window.location.pathname
  }

  getPriceRangeRemovalHref () {
    const params = new URLSearchParams(this.searchParams.current.toString())

    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')
    params.delete('page')

    const nextQuery = params.toString()

    return nextQuery
      ? `${window.location.pathname}?${nextQuery}`
      : window.location.pathname
  }

  getActivePriceRangeLabel (filter = {}) {
    const minValue =
      filter.min != null
        ? this.formatFacetMoney(filter.min)
        : this.formatFacetMoney(this.getMinPriceRangeValue())
    const maxValue =
      filter.max != null
        ? this.formatFacetMoney(filter.max)
        : this.formatFacetMoney(this.getMaxPriceRangeValue())

    return `${minValue} - ${maxValue}`
  }

  formatFacetMoney (value) {
    return window.BAO.Utils.formatMoney(Number(value) * 100).replace('.00', '')
  }

  getMinPriceRangeValue () {
    const priceRangeInput = document.querySelector(
      '#CollectionFiltersMobileForm input[name="filter.v.price.gte"], #CollectionFiltersForm input[name="filter.v.price.gte"]',
    )

    return Number(priceRangeInput?.min || 0)
  }

  getMaxPriceRangeValue () {
    const priceRangeInput = document.querySelector(
      '#CollectionFiltersMobileForm input[name="filter.v.price.lte"], #CollectionFiltersForm input[name="filter.v.price.lte"]',
    )

    return Number(priceRangeInput?.max || 0)
  }

  getClearAllHref () {
    return window.location.pathname
  }

  getClearAllLabel () {
    const existingClearAllLink = this.querySelector(
      '[data-collection-filters-el="active-facets"] .clc-ProductGridFacets_ActiveLink-plain',
    )

    return existingClearAllLink?.textContent?.trim() || 'Clear all'
  }

  getPaginationHref (page) {
    const params = new URLSearchParams(this.searchParams.current.toString())

    if (page <= 1) {
      params.delete('page')
    } else {
      params.set('page', String(page))
    }

    return `${window.location.pathname}?${params.toString()}`
  }

  deferBrowseProductRender (products) {
    if (this.pendingBrowseRenderFrame) {
      cancelAnimationFrame(this.pendingBrowseRenderFrame)
    }

    this.pendingBrowseProducts = products
    this.pendingBrowseRenderFrame = requestAnimationFrame(() => {
      this.pendingBrowseRenderFrame = null
      const hasPendingPromotions = this.pendingBrowseProducts?.some((item) => {
        return item?.itemType === 'promotion'
      })

      if (
        !this.pendingBrowseProducts ||
        !this.templateProductCard ||
        (hasPendingPromotions && !this.templatePromotionCard)
      ) {
        return
      }

      const nextProducts = this.pendingBrowseProducts
      this.pendingBrowseProducts = null

      this.revealBrowsePromotionItemsWhenReady(
        this.renderBrowseProducts(nextProducts, {
          intent:
            this.collectionBrowseStateLayer?.state?.pagination?.lastIntent,
        }),
      )
    })
  }

  getBrowsePaginationIntent (anchor) {
    if (!this.shouldUseConstructorBrowse || !anchor) {
      return null
    }

    if (anchor.closest('.clc-ProductGrid_Pagination-loadPrevious')) {
      return 'prepend-previous'
    }

    if (anchor.closest('.clc-ProductGrid_Pagination-loadMore')) {
      return 'append-next'
    }

    return 'replace'
  }

  scrollProductGridToTop () {
    if (!this.els['product-grid']?.element) {
      return
    }

    window.requestAnimationFrame(() => {
      const scrollTarget = this.getProductGridScrollTarget()
      if (!scrollTarget) {
        return
      }

      const top = Math.max(
        0,
        window.scrollY +
          scrollTarget.getBoundingClientRect().top -
          this.getProductGridScrollOffset(),
      )

      window.scrollTo({ top, behavior: 'smooth' })
    })
  }

  getProductGridScrollTarget () {
    const productGrid = this.els['product-grid']?.element
    if (!productGrid) {
      return null
    }

    const visibleItem = Array.from(
      productGrid.querySelectorAll('.prd-List_Item'),
    ).find((item) => this.isVisibleProductGridItem(item))

    return visibleItem || productGrid
  }

  isVisibleProductGridItem (item) {
    if (!(item instanceof HTMLElement)) {
      return false
    }

    if (
      item.classList.contains('prd-ProductSkeleton') ||
      item.getAttribute('aria-hidden') === 'true'
    ) {
      return false
    }

    const styles = window.getComputedStyle(item)

    return styles.display !== 'none' && styles.visibility !== 'hidden'
  }

  getProductGridScrollOffset () {
    return (
      this.getProductGridHeaderOffset() + this.getMobileHeaderOffset() + 12
    )
  }

  getProductGridHeaderOffset () {
    const header = document.querySelector('.hd-Banner_Container')

    return header instanceof HTMLElement ? header.offsetHeight : 0
  }

  getMobileHeaderOffset () {
    if (!this.isMobileProductGridLayout()) {
      return 0
    }

    const headerBody = document.querySelector('.hd-Banner_Body')

    return headerBody instanceof HTMLElement ? headerBody.offsetHeight : 0
  }

  isMobileProductGridLayout () {
    const fixedFilters = this.querySelector('.clc-ProductGrid_FixedFilters')

    if (!(fixedFilters instanceof HTMLElement)) {
      return false
    }

    return window.getComputedStyle(fixedFilters).display !== 'none'
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${
        searchParams && '?'.concat(searchParams.toString())
      }`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())

    if (val) {
      this.els['product-grid']?.element?.removeAttribute(
        'data-cnstrc-zero-result',
      )
    }
  }

  get shouldUseConstructorBrowse () {
    if (!this.client || window.theme?.constructorIo?.error) {
      return false
    }

    if (!this.browseFilterName || !this.browseFilterValue) {
      return false
    }

    return true
  }

  get shouldRenderNativeProductGrid () {
    return !this.shouldUseConstructorBrowse
  }

  get hasActiveBrowseFilters () {
    return Array.from(this.searchParams.current.keys()).some((key) => {
      return key.startsWith('filter.')
    })
  }

  get client () {
    return window.theme?.constructorIo?.client || null
  }

  get collectionBrowseEventBus () {
    return window.theme?.collectionBrowse?.eventBus || null
  }

  get collectionBrowseStateLayer () {
    return window.theme?.collectionBrowse?.stateLayer || null
  }

  get tracker () {
    return this.client?.tracker || null
  }

  get cnstrcDataScript () {
    return document.getElementById('cnstrc-data')
  }

  createCnstrcDataScript () {
    const script = document.createElement('script')
    script.id = 'cnstrc-data'
    script.type = 'application/json'
    this.prepend(script)

    return script
  }

  get templateProductCard () {
    return document.getElementById('templateProductCard')
  }

  get templatePromotionCard () {
    return document.getElementById('templatePromotionCard')
  }

  get browseFilterName () {
    return this.els['product-grid']?.element?.dataset.cnstrcFilterName || ''
  }

  get browseFilterValue () {
    return this.els['product-grid']?.element?.dataset.cnstrcFilterValue || ''
  }

  get currentPage () {
    return Number(this.searchParams.current.get('page')) || 1
  }

  get currentSortBy () {
    return this.searchParams.current.get('sort_by') || 'best-selling'
  }

  get loadPreviousLabel () {
    return (
      this.els['product-grid']?.element?.dataset.loadPreviousLabel ||
      'Load previous'
    )
  }

  get loadMoreLabel () {
    return (
      this.els['product-grid']?.element?.dataset.loadMoreLabel || 'Load more'
    )
  }

  get backTopLabel () {
    return (
      this.els['product-grid']?.element?.dataset.backTopLabel || 'Back to top'
    )
  }

  get showingCountLabel () {
    return (
      this.els['product-grid']?.element?.dataset.showingCountLabel ||
      'Showing __FROM__ to __COUNT__ of __TOTAL__'
    )
  }

  get resultsPerPage () {
    return this.collectionBrowseStateLayer?.state?.pagination?.size || 24
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}

const initializeCollectionFilters = () => {
  document.querySelectorAll('collection-filters').forEach((element) => {
    if (element.isConnected && !element.hasBeenInitialised) {
      element.mount()
    }
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCollectionFilters, {
    once: true,
  })
} else {
  initializeCollectionFilters()
}
